import { useMemo } from 'react';
import useSWR, { mutate } from 'swr';
import type { SWRConfiguration } from 'swr';

import { fetcher, endpoints } from '@/lib/axios';
import { ENTREPRENEURSHIP_CATEGORY_SLUG } from '@/lib/constants';
import type {
  ICategory,
  ICategoriesResponse,
} from '@/types/category';

// ----------------------------------------------------------------------

const swrOptions: SWRConfiguration = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  shouldRetryOnError: false,
};

// ----------------------------------------------------------------------

/**
 * Fetches all active root categories together with their active subcategories.
 * Endpoint: GET /api/v1/categories
 *
 * The "Entreprenariat" category is filtered out: it has its own dedicated
 * /entrepreneurship page and must not be reachable through the generic
 * category navigation (catalog selector, search bar, navbar, footer).
 */
export function useGetCategories() {
  const url = endpoints.categories.list;

  const { data, isLoading, error, isValidating } = useSWR<ICategoriesResponse>(
    url,
    fetcher,
    swrOptions
  );

  const memoizedValue = useMemo(() => {
    const categories = ((data?.data ?? []) as ICategory[]).filter(
      (category) => category.slug !== ENTREPRENEURSHIP_CATEGORY_SLUG
    );

    return {
      categories,
      categoriesLoading: isLoading,
      categoriesError: error as Error | undefined,
      categoriesValidating: isValidating,
      categoriesEmpty: !isLoading && !categories.length,
    };
  }, [data, error, isLoading, isValidating]);

  return memoizedValue;
}


// ----------------------------------------------------------------------

/** Programmatically revalidate the categories list (e.g. after admin mutation). */
export async function refreshCategories(): Promise<void> {
  await mutate(endpoints.categories.list);
}
